import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { node, string, func, shape, number, bool } from 'prop-types';
import events from 'utils/events';
import { Caret } from 'components/Icons';
import classnames from 'classnames';

import './Dropdown.scss';

export default class Dropdown extends PureComponent {
    static propTypes = {
        className: string,
        theme: shape({
            content: string,
            children: string,
        }),
        label: string.isRequired,
        children: node,
        onClick: func,
        count: number,
        showCount: bool,
    };

    static defaultProps = {
        className: null,
        count: 0,
        theme: {},
        children: null,
        onClick: null,
        showCount: true,
    };

    state = {
        active: false,
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevState.active && !this.state.active) {
            events.removeEventsFromDocument(this.getDocumentEvents());
        }
        if (snapshot) {
            events.addEventsToDocument(this.getDocumentEvents());
        }
    }

    componentWillUnmount() {
        if (this.state.active) {
            events.removeEventsFromDocument(this.getDocumentEvents());
        }
    }

    getSnapshotBeforeUpdate(prevProps, prevState) {
        return !prevState.active && this.state.active;
    }

    getDocumentEvents = () => ({
        click: this.handleDocumentClick,
        touchend: this.handleDocumentClick,
    });

    close = () => {
        if (this.state.active) {
            this.setState({ active: false });
        }
    };

    open = () => {
        if (!this.state.active) {
            this.setState({ active: true });
        }
    };

    handleMouseDown = evt => {
        events.pauseEvent(evt);
    };

    handleDocumentClick = evt => {
        if (
            this.state.active &&
            // eslint-disable-next-line react/no-find-dom-node
            !events.targetIsDescendant(evt, findDOMNode(this))
        ) {
            this.close();
        }
    };

    handleClick = evt => {
        events.pauseEvent(evt);
        if (this.state.active) {
            this.close(evt);
        } else {
            this.open(evt);
        }
        if (this.props.onClick) this.props.onClick(evt);
    };

    render() {
        const {
            className,
            theme = {},
            label,
            count,
            showCount,
            ...otherProps
        } = this.props;
        const { active } = this.state;
        const rootClassnames = classnames(
            'dropdown',
            {
                'dropdown_has-value': count,
                dropdown_open: active,
            },
            className,
        );

        const contentClassnames = classnames(
            'dropdown__content_container',
            {
                'dropdown__content--active': active,
            },
            theme.content,
        );

        const children = React.Children.map(this.props.children, child =>
            React.cloneElement(child, {
                closeDropdown: this.close,
            }),
        );

        return (
            <div {...otherProps} className={rootClassnames} tabIndex="-1">
                <button
                    className="dropdown__title"
                    data-filter-name={label}
                    onClick={this.handleClick}
                    onMouseDown={this.handleMouseDown}
                >
                    <span className="dropdown__title__name">{label}</span>
                    {showCount && count > 0 ? (
                        <span className="dropdown__title__count">
                            ({count})
                        </span>
                    ) : (
                        <Caret
                            className="dropdown__title__caret"
                            viewBox="0 7 32 20"
                        />
                    )}
                </button>

                <div ref={this.handleContentRef} className={contentClassnames}>
                    <div className="dropdown__content">
                        <div className="dropdown__content__list">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
